<script setup>
import { onMounted, onUnmounted, ref, watch, computed, nextTick } from 'vue'
import { useResultsStore } from '../stores/results-fetcher.js'
import { useStateStore } from '../stores/state.js'
import { useRoute } from "vue-router";
import { formatGapTime } from '../utils/gap-formatter.js'
import { formatMarkerText } from '../utils/marker-formatter.js'
import EventSwitcher from './EventSwitcher.vue'

const resultsStore = useResultsStore()
const stateStore = useStateStore()
const route = useRoute()
const eventAlias = route.params.eventAlias
resultsStore.fetchResults(eventAlias)
resultsStore.fetchEventDetails(eventAlias)
stateStore.fetchEvents()

const now = ref(Date.now())
let refreshResultsTimer = setInterval(() => {
      resultsStore.fetchResults(route.params.eventAlias)
    }, 30000)
let timer = null

// Search functionality
const searchQuery = ref('')
const showDropdown = ref(false)
const selectedResultId = ref(null)

// Track recently changed rows by result.id and the timestamp of change
const recentlyChanged = ref({})
const superLive = ref(true)
const showElapsedTime = ref(true)

// Helper to compare results and mark changed rows
let lastResults = []

watch(
  () => resultsStore.results.map(r => ({ id: r.id, score: r.score, finish_time: r.finish_time, finish_time_gps: r.finish_time_gps, dnf: r.dnf, abs: r.abs, categoryPosition: r.categoryPosition, penaltySeconds: r.penaltySeconds, penaltyComment: r.penaltyComment })),
  (newResults) => {
    const nowTime = Date.now()
    // Skip check if lastResults is empty (first load)
    if (lastResults.length === 0) {
      lastResults = JSON.parse(JSON.stringify(newResults))
      return
    }
    newResults.forEach((r) => {
      const prev = lastResults.find(lr => lr.id === r.id)
      if (
        !prev ||
        prev.score !== r.score ||
        prev.finish_time !== r.finish_time ||
        prev.finish_time_gps !== r.finish_time_gps ||
        prev.dnf !== r.dnf ||
        prev.abs !== r.abs ||
        prev.categoryPosition !== r.categoryPosition
      ) {
        recentlyChanged.value[r.id] = nowTime
      }
    })
    lastResults = JSON.parse(JSON.stringify(newResults))
  },
  { immediate: true }
)

// Computed property for filtered results based on search
const filteredResults = computed(() => {
  if (!searchQuery.value.trim()) {
    return []
  }
  
  const query = searchQuery.value.trim().toLowerCase()
  return resultsStore.results.filter(result => {
    const nameMatch = result.name.toLowerCase().includes(query)
    const numberMatch = result.number.toString().includes(query)
    return (nameMatch || numberMatch) && result.category_id !== 10
  }).slice(0, 10) // Limit to 10 results for performance
})

// Watch search query to show/hide dropdown
watch(searchQuery, (newQuery) => {
  showDropdown.value = newQuery.trim().length > 0 && filteredResults.value.length > 0
})

// Function to scroll to and highlight a result
function selectResult(result) {
  selectedResultId.value = result.id
  searchQuery.value = ''
  showDropdown.value = false
  
  // Add highlight with timestamp
  recentlyChanged.value[result.id] = Date.now()
  
  // Scroll to the result
  nextTick(() => {
    const resultRow = document.querySelector(`tr[data-result-id="${result.id}"]`)
    if (resultRow) {
      resultRow.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    }
  })
}

// Function to handle clicking outside dropdown
function handleClickOutside(event) {
  if (!event.target.closest('.search-container')) {
    showDropdown.value = false
  }
}

// Clean up highlights after 30 seconds
const CLEANUP_INTERVAL = 1000
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  timer = setInterval(() => {
    now.value = Date.now()
    // Remove highlights older than 30 seconds
    const cutoff = Date.now() - 30 * 1000; // 30 seconds
    for (const id in recentlyChanged.value) {
      if (recentlyChanged.value[id] < cutoff) {
        delete recentlyChanged.value[id]
      }
    }
  }, CLEANUP_INTERVAL)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  if (timer) clearInterval(timer)
  if (refreshResultsTimer) clearInterval(refreshResultsTimer)
})


function calculateTimeFromStart(result) {
  const startTime = new Date(result.start_time)
  const elapsed = new Date(now.value - startTime)
  return getHHMMSSString(elapsed)
}

function timeFromStart(result) {
  const startTime = new Date(result.start_time)
  const now = new Date()
  const elapsed = new Date(now - startTime)
  return elapsed
}

function timeFromStartToSplit(result, split) {
    const startTime = new Date(result.start_time)
    const splitTime = new Date(split.time)
    const elapsed = new Date(splitTime - startTime)
    return elapsed
}

function getHHMMSSString(date) {
  // Handle negative or zero elapsed time - don't display
  const timestamp = date instanceof Date ? date.getTime() : (typeof date === 'number' ? date : 0)
  if (timestamp <= 0) {
    return ''
  }
  const hours = Math.floor(timestamp / (1000 * 60 * 60))
  const dateObj = date instanceof Date ? date : new Date(timestamp)
  const minutes = String(dateObj.getUTCMinutes()).padStart(2, '0')
  const seconds = String(dateObj.getUTCSeconds()).padStart(2, '0')
  return `${hours}:${minutes}:${seconds}`
}

function getResultClass(result) {
  if (result.dnf) return 'badge bg-danger'
  if (result.abs) return 'badge bg-secondary'
  if (result.finish_time || result.finish_time_gps) return ''
  return 'result-upcoming'
}

function sortedResults() {
    return resultsStore.results.slice().filter(r => r.category_id !== 10).sort((a, b) => {
        if (a.abs) {
            return 1
          }
          if (b.abs) {
            return -1
          }
          if (a.dnf) {
            return 1
          }
          if (b.dnf) {
            return -1
          }

          // Check if any runner has point-based scores to determine sorting method
          const hasPointScores = resultsStore.results.some(runner => runner.pointScore !== null && runner.pointScore > 0)
          
          // If we have point-based scores, sort by points first (higher points = better position)
          if (hasPointScores) {
            const aPoints = a.pointScore || 0
            const bPoints = b.pointScore || 0
            
            // If both have points or both don't have points, compare them
            if ((aPoints > 0) === (bPoints > 0)) {
              if (aPoints > 0) {
                // Both have points - higher points wins (sort descending)
                if (aPoints !== bPoints) {
                  return bPoints - aPoints
                }
                // If points are equal, sort by time
                if (a.elapsedTime && b.elapsedTime) {
                  return a.elapsedTime - b.elapsedTime
                }
              } else {
                // Both have no points - sort by time
                if (a.elapsedTime && b.elapsedTime) {
                  return a.elapsedTime - b.elapsedTime
                }
              }
            } else {
              // One has points, one doesn't - the one with points wins
              return bPoints > 0 ? 1 : -1
            }
          } else {
            // No point-based scores - use original live sorting logic
            if (a.elapsedTime && b.elapsedTime) {
              return a.elapsedTime - b.elapsedTime
            }
          }

          if (a.finish_time || a.finish_time_gps || b.finish_time || b.finish_time_gps){
            if (a.finish_time || a.finish_time_gps) {
                return -1
            }
            if (b.finish_time || b.finish_time_gps) {
                return 1
            }
        } else {
            return compareElapsedTimeAtLastControl(a, b)
        }
    })
}
function compareElapsedTimeAtLastControl(a, b) {
    // If both have no lastMarker, compare by start time
    if (!a.lastMarker && !b.lastMarker) {
        return a.number - b.number
    }
    if (!a.lastMarker) {
        // console.log("a has no lastMarker, comparing by timeFromStart", timeFromStart(a), b.lastSplit.time)
        return 1
    }
    if (!b.lastMarker) {
        // console.log("b has no lastMarker, comparing by a.lastSplit.time", a.lastSplit.time, timeFromStart(b))
        return -1
    }
    // Compare by elapsed time at last control
    if (a.lastMarker.id == b.lastMarker.id) {
        let aDate = new Date(a.lastSplit.time.replace(' ', 'T') + 'Z')
        let bDate = new Date(b.lastSplit.time.replace(' ', 'T') + 'Z')
        if (aDate.getTime() == bDate.getTime()) {
            return a.number - b.number
        } else {
            return timeFromStartToSplit(a, a.lastSplit) - timeFromStartToSplit(b, b.lastSplit)
        }
    } else {
        if (a.lastMarker.sort > b.lastMarker.sort) {
            if (a.lastMarker.sort - 1 == b.lastMarker.sort) {
                if (a.splits[b.lastMarker.id]) {
              // If a was faster on the shared marker and is still under b's time at their next marker, show a before b
              if (a.splits[b.lastMarker.id].time < b.lastSplit.time && timeFromStart(a) < timeFromStartToSplit(b, b.lastSplit)) {
                return -1
                    } 
                }
            }
            return -1
        } else {
            if (b.lastMarker.sort - 1 == a.lastMarker.sort) {
                if (b.splits[a.lastMarker.id]) {
              // If a was faster on the shared marker and is still under b's time at their next marker, show a before b
              if (a.lastSplit.time < b.splits[a.lastMarker.id].time && timeFromStart(a) < timeFromStartToSplit(b, b.lastSplit)) {
                return -1
                    } 
                }
            }
            return 1
        }
    }
}

function getMarkersAsArray() {
  if (!resultsStore.markersMap) return []
  return Object.values(resultsStore.markersMap).sort((a, b) => a.sort - b.sort)
}

function isLastMarkerFinish() {
  const markers = getMarkersAsArray()
  if (markers.length === 0) return false
  const lastMarker = markers[markers.length - 1]
  return lastMarker.type == 8 || lastMarker.type == 93
}

function formatPenaltyLabel(penaltySeconds) {
  const totalSeconds = Math.round(Number(penaltySeconds) || 0)
  if (totalSeconds <= 0) return ''

  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  if (hours > 0) {
    const minutesPart = minutes > 0 ? `${minutes}${seconds > 0 ? `:${String(seconds).padStart(2, '0')}` : ''}min` : ''
    const secondsPart = minutes === 0 && seconds > 0 ? `${seconds}s` : ''
    return `+${hours}h${minutesPart || secondsPart ? minutesPart || secondsPart : ''}`
  }

  if (minutes > 0) {
    if (seconds === 0) return `+${minutes}min`
    return `+${minutes}:${String(seconds).padStart(2, '0')}min`
  }

  return `+${seconds}s`
}
</script>

<template>
  <div>
    <EventSwitcher />
    <h1>Wyniki na żywo</h1>
    
    <!-- Search functionality -->
    <div class="search-container mb-3">
      <div class="position-relative">
        <div class="search-input-wrapper">
          <svg class="search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" fill="currentColor"/>
          </svg>
          <input 
            type="text" 
            v-model="searchQuery" 
            class="form-control search-input" 
            placeholder="Wyszukaj zawodnika po numerze lub nazwie..."
            @focus="showDropdown = searchQuery.trim().length > 0 && filteredResults.length > 0"
          />
        </div>
        
        <!-- Search dropdown -->
        <div v-if="showDropdown" class="search-dropdown">
          <div 
            v-for="result in filteredResults" 
            :key="result.id"
            class="search-dropdown-item"
            @click="selectResult(result)"
          >
            <span class="badge bg-secondary me-2">{{ result.number }}</span>
            <span>{{ result.name }}</span>
          </div>
        </div>
      </div>
    </div>
    
    <div class="table-scroll">
        <div class="settings">
            <!-- <span class="badge bg-secondary">Event ID: {{ eventId }}</span> -->
            <span class="badge bg-info">Aktualizacja co 30 sekund</span>
            <span class="badge bg-success">Czas: {{ new Date(now).toLocaleTimeString() }}</span>
            <!-- checkbox to switch between super live results -->
            <input type="checkbox" v-model="superLive" class="form-check-input" style="display: none;" />
            <label class="form-check-label" style="display: none;">Super Live</label>
            <!-- checkbox to switch between elapsed time and arrival time -->
            <input type="checkbox" v-model="showElapsedTime" class="form-check-input" />
            <label class="form-check-label">Czas narastająco</label>

        </div>
      <table class="table table-striped">
        <thead>
          <tr>
            <th>#</th>
            <th>Zawodnik</th>
            <th v-if="resultsStore.shouldShowCategoryColumn()">kat.</th>
            <th v-if="resultsStore.shouldShowPointsColumn()">Punkty</th>
            <th>Start</th>
            <th v-for="marker in getMarkersAsArray()" :key="marker.id" class="marker-column">
                <span v-html="formatMarkerText(marker.text)"></span>
            </th>
            <th v-if="!isLastMarkerFinish()">Meta</th>
            <th>Czas</th>
            <th>Strata</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(result, index) in sortedResults()"
            :key="result.id"
            :data-result-id="result.id"
            :class="{ 'row-changed': recentlyChanged[result.id] }"
          >
            <td>{{ index + 1 }}</td>
            <td><span class="badge bg-secondary">{{ result.number }}</span> {{ result.name }}</td>
            <td v-if="resultsStore.shouldShowCategoryColumn()">
              {{ result.categoryPosition }}
            </td>
            <td v-if="resultsStore.shouldShowPointsColumn()">
              {{ result.pointScore || 0 }}
            </td>
            <td>{{ result.start_time ? new Date(result.start_time).toLocaleTimeString() : '' }}</td>
            <td v-for="(marker, index) in getMarkersAsArray()" :key="marker.id" class="marker-cell">
              <span v-if="index == getMarkersAsArray().length - 1 && isLastMarkerFinish()">
                  {{ result.finish_time ? new Date(result.finish_time).toLocaleTimeString() : (result.finish_time_gps ? new Date(result.finish_time_gps).toLocaleTimeString() : '')}}
                  <span v-if="!result.finish_time && result.finish_time_gps" class="badge bg-warning">GPS</span>
              </span>
                <span v-else-if="result.splits[marker.id]"> 
                    <span v-if="showElapsedTime">
                        {{ result.splits[marker.id].time ? getHHMMSSString(timeFromStartToSplit(result, result.splits[marker.id])) : '--:--:--' }}
                    </span>
                    <span v-else>
                        {{ result.splits[marker.id].time ? new Date(result.splits[marker.id].time).toLocaleTimeString() : '--:--:--' }}
                    </span>
                </span>
            </td>
            <td v-if="!isLastMarkerFinish()" class="marker-cell">
              {{ result.finish_time ? new Date(result.finish_time).toLocaleTimeString() : (result.finish_time_gps ? new Date(result.finish_time_gps).toLocaleTimeString() : '')}}
              <span v-if="!result.finish_time && result.finish_time_gps" class="badge bg-warning">GPS</span>
            </td>
            <td>
              <span v-if="result.dnf || result.abs" :class="getResultClass(result)">{{ result.abs? "DNS" : "DNF" }}</span>
              <span v-else :class="getResultClass(result)">
                {{ result.finish_time ? result.timeScore : (result.finish_time_gps ? result.timeScore : (result.start_time ? calculateTimeFromStart(result) : '--:--:--')) }}
              </span>
              <span
                v-if="result.penaltySeconds"
                class="badge bg-warning text-dark penalty-badge"
                :title="result.penaltyComment || 'Kara czasowa'"
              >
                {{ formatPenaltyLabel(result.penaltySeconds) }}
              </span>
            </td>
            <td>
              {{ formatGapTime(resultsStore.getGapToLeader(result)) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
.table {
    text-align: left;
}

/* Center-align marker column headers */
.marker-column {
    text-align: center;
}

/* Center-align marker column data cells */
.table td.marker-cell {
    text-align: center;
}
.result-upcoming {
  font-style: italic;
}
.table-scroll {
  width: 100%;
  max-height: 80vh;
  overflow-x: auto;
  overflow-y: auto;
}

/* Sticky table header */
.table thead th {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #f8f9fa; /* Bootstrap table header background */
}
.table.table-striped > tbody > tr.row-changed > td,
tbody tr.row-changed > td {
  background: #ebd0b2 !important;
  transition: background 1s;
  font-weight: bold !important;
  animation: highlight-row 1s linear;
}
@keyframes highlight-row {
  0% { background: #ffa754; }
  100% { background: #ebd0b2; }
}

/* Search functionality styles */
.search-container {
  max-width: 400px;
}

.search-input-wrapper {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  pointer-events: none;
  z-index: 10;
}

.search-input {
  padding-left: 40px;
}

.search-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #dee2e6;
  border-top: none;
  border-radius: 0 0 0.375rem 0.375rem;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.search-dropdown-item {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  border-bottom: 1px solid #f8f9fa;
}

.search-dropdown-item:hover {
  background-color: #f8f9fa;
}

.search-dropdown-item:last-child {
  border-bottom: none;
}

.penalty-badge {
  margin-left: 6px;
  vertical-align: middle;
}
</style>